import { Card } from "@/src/components/cards";
import { openConfirmDialog, openDrawer } from "@/src/components/dialogs";
import { FetchableList } from "@/src/components/fetchableLists";
import {
  antecedentsCategoriesIds,
  FamilyMember,
  familyMemberRelationships,
  Patient,
  RefAnswer,
} from "@/src/data/allTypes";
import apiFetch from "@/src/utils/apiFetch";
import {
  getAge,
  getItemId,
  getName,
  jsonAbstract,
  uid,
} from "@/src/utils/utils";
import { useState } from "react";
import { AntecedentsCard } from "./antecedentsCard";
import { FamilyMemberAntecedentsDrawer } from "./antecedentsDrawer";
import { Button } from "@/src/components/buttons";
import moment from "moment";

export function FamilyMemberCard({
  familyMember,
  originalPatient,
  onAntecedentsChange,
  onDelete,
}: {
  familyMember: FamilyMember;
  originalPatient: Patient;
  onAntecedentsChange: (newValue: RefAnswer[]) => void;
  onDelete: () => void;
}) {
  const [reload, setReload] = useState(0);

  const relationship = familyMemberRelationships[familyMember.relationship];


  return (
    <Card
      key={uid()}
      color="blue"
      onClick={async () => {
        openFamilyMemberAntecedentsDrawer({
          familyMember,
          antecedents: null,
          onAntecedentsChange,
        });
      }}
      onDelete={async () => {
        await openConfirmDialog({
          title: "Supprimer le membre familial",
          content: "Voulez-vous vraiment supprimer ce membre de la famille ?",
          onConfirm: async () => {
            await apiFetch("familyMembers", "deleteFamilyMember", {
              patientId: getItemId(originalPatient),
              familyMemberId: getItemId(familyMember),
            });
            onDelete();
            setReload((prev) => prev + 1);
          },
        });
      }}
    >
      <div className="c1 gap10" style={{ width: "100%" }}>
        <div>
          <div className="bold">{relationship?.name}</div>
          {getName(familyMember?.patient)?.trim() != "" && (
            <div className="gray">{getName(familyMember?.patient)}</div>
          )}
          <div className="">{familyMember?.patient?.notes}</div>
        </div>
        <FetchableList
          queryKey={[
            "familyAntecedents",
            getItemId(familyMember.patient),
            reload,
          ]}
          queryFn={() =>
            apiFetch("refAnswers", "loadMany", {
              categories: antecedentsCategoriesIds,
              patientId: getItemId(familyMember.patient),
            })
          }
          emptyChild={
            <Button
              type="tertiary"
              onClick={() => {
                openFamilyMemberAntecedentsDrawer({
                  familyMember,
                  antecedents: null,
                  onAntecedentsChange,
                });
              }}
            >
              Ajouter antécédents
            </Button>
          }
        >
          {(data) => {
            return (
              <div className="c1 gap5">
                {data?.map((antecedent) => (
                  <AntecedentsCard
                    key={uid()}
                    antecedent={antecedent}
                    patient={familyMember.patient!}
                    onClick={async () => {
                      openFamilyMemberAntecedentsDrawer({
                        familyMember,
                        antecedents: null,
                        onAntecedentsChange,
                      });
                    }}
                  ></AntecedentsCard>
                ))}
              </div>
            );
          }}
        </FetchableList>
      </div>
    </Card>
  );
}

export async function openFamilyMemberAntecedentsDrawer({
  familyMember,
  antecedents,
  onAntecedentsChange,
}: {
  familyMember: FamilyMember;
  antecedents: RefAnswer[] | null | undefined;
  onAntecedentsChange: (newValue: RefAnswer[]) => void;
}) {
  const patient = familyMember.patient;
  if (!patient) throw new Error("Patient not found");

  let newAntecedents: RefAnswer[] = jsonAbstract(
    antecedents ||
      (await apiFetch("refAnswers", "loadMany", {
        categories: antecedentsCategoriesIds,
        patientId: getItemId(patient),
      }))
  );

  await openDrawer({
    id: "familyMemberAntecedentsDrawer",
    title: `Antécédents ${familyMember.relationship}`,
    additionalClasses: ["small"],
    content: (
      <FamilyMemberAntecedentsDrawer
        antecedents={newAntecedents}
        patient={patient}
        onAntecedentsChange={(newValue) => {
          newAntecedents = newValue;
        }}
      />
    ),
    onConfirm: async () => {
      await apiFetch(
        "refAnswers",
        "addMany",
        {
          refAnswers: newAntecedents,
        },
        "Erreur lors de l'ajout des antécédents"
      );
      onAntecedentsChange(newAntecedents);
    },
  });
}
